import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Plus, Search, Menu, X, UserPlus, ChevronRight, Sparkles, Mail } from "lucide-react";
import { LogoLockup } from "./Logo";
import { useJericho } from "./jericho/JerichoEasterEgg";
import { useMemberAuth } from "../hooks/useMemberAuth";
import { useDispatchUnreadCount } from "../pages/Inbox";
import {
  isFoundingMember, FOUNDING_GOLD, FOUNDING_GOLD_DIM, FOUNDING_GOLD_GLOW_BG,
} from "./founding";
import { CommandTrigger, MobileCommandTrigger } from "../command/CommandTrigger";

const navLinkCls = ({ isActive }) =>
  `font-mono text-xs uppercase tracking-[0.25em] transition-colors ${
    isActive ? "text-[#D92D20]" : "text-[#A39E93] hover:text-[#E4DFD3]"
  }`;

const mobileLinkCls = ({ isActive }) =>
  `block py-4 border-b border-[#2D2A26] font-mono text-sm uppercase tracking-[0.25em] transition-colors ${
    isActive ? "text-[#D92D20]" : "text-[#E4DFD3] hover:text-[#D92D20]"
  }`;

/**
 * Inbox link with an unread-count badge. Only renders if the viewer is a
 * signed-in member. Uses the lightweight `useDispatchUnreadCount` hook so
 * Public/anon visitors don't make API calls.
 */
const InboxNavLink = () => {
  const { isMember } = useMemberAuth();
  const unread = useDispatchUnreadCount();
  if (!isMember) return null;
  return (
    <NavLink to="/inbox" className={navLinkCls} data-testid="nav-inbox">
      <span className="inline-flex items-center gap-1.5">
        <Mail size={12} /> // Inbox
        {unread > 0 && (
          <span className="inline-flex items-center justify-center bg-[#D92D20] text-white text-[9px] font-mono px-1.5 rounded-sm" data-testid="nav-inbox-unread-badge">
            {unread}
          </span>
        )}
      </span>
    </NavLink>
  );
};

const MobileInboxEntry = ({ onNavigate }) => {
  const { isMember } = useMemberAuth();
  const unread = useDispatchUnreadCount();
  if (!isMember) return null;
  return (
    <NavLink to="/inbox" onClick={onNavigate} className={mobileLinkCls} data-testid="mobile-nav-inbox">
      <span className="inline-flex items-center gap-2">
        <Mail size={14} /> // Inbox
        {unread > 0 && (
          <span className="inline-flex items-center justify-center bg-[#D92D20] text-white text-[10px] font-mono px-2 rounded-sm" data-testid="mobile-nav-inbox-unread">
            {unread}
          </span>
        )}
      </span>
    </NavLink>
  );
};

/**
 * Mobile-only Members nav entry. Two states:
 *   1. Anonymous     → "MEMBERS · Become a Subscriber →" (Archive red)
 *   2. Signed in     → callsign + designation + clearance stars
 *      Founding Members get a subtle gold accent; everyone else uses
 *      the Archive red. Tap routes to /members (the member dossier).
 */
const MobileMembersEntry = ({ onNavigate }) => {
  const { member, isMember, loading } = useMemberAuth();
  if (loading) {
    return (
      <div
        className="block py-4 border-b border-[#2D2A26] font-mono text-sm uppercase tracking-[0.25em] text-[#757065]"
        data-testid="mobile-nav-members-loading"
      >
        // Members
      </div>
    );
  }

  if (!isMember) {
    return (
      <Link
        to="/members"
        onClick={onNavigate}
        data-testid="mobile-nav-members-anon"
        className="block py-4 border-b border-[#2D2A26] font-mono uppercase transition-colors text-[#D92D20] hover:text-[#E4DFD3]"
      >
        <div className="text-sm tracking-[0.25em]">// Members</div>
        <div className="mt-1 flex items-center gap-1.5 text-[10px] tracking-[0.3em] text-[#A39E93]">
          <UserPlus size={11} /> Become a Subscriber
          <ChevronRight size={11} className="ml-auto" />
        </div>
      </Link>
    );
  }

  const isFounding = isFoundingMember(member);
  const stars = Math.max(0, Math.min(5, Number(member.clearance_level || 0)));
  const accent = isFounding ? FOUNDING_GOLD : "#D92D20";
  const designation = isFounding
    ? "Founding Member"
    : (member.designation && member.designation !== "Subscriber"
        ? member.designation
        : (member.tier === "member" ? "Member" : "Subscriber"));

  return (
    <Link
      to="/members"
      onClick={onNavigate}
      data-testid="mobile-nav-members-identity"
      className="block py-4 border-b border-[#2D2A26] transition-colors"
      style={isFounding
        ? {
            borderLeft: `2px solid ${FOUNDING_GOLD_DIM}`,
            paddingLeft: "0.75rem",
            marginLeft: "-0.75rem",
            backgroundColor: FOUNDING_GOLD_GLOW_BG,
          }
        : {
            borderLeft: `2px solid ${accent}`,
            paddingLeft: "0.75rem",
            marginLeft: "-0.75rem",
          }}
    >
      <div className="flex items-baseline gap-2 font-mono uppercase">
        <span
          className="text-sm tracking-[0.25em] truncate"
          style={{ color: accent }}
          data-testid="mobile-nav-members-callsign"
        >
          {member.callsign || "—"}
        </span>
        {stars > 0 && (
          <span
            className="text-[11px] tracking-wider shrink-0"
            style={{ color: accent }}
            aria-label={`Clearance level ${stars} of 5`}
            data-testid="mobile-nav-members-stars"
          >
            {"★".repeat(stars)}
          </span>
        )}
        <ChevronRight size={11} className="ml-auto text-[#A39E93]" />
      </div>
      <div className="mt-1 flex items-center gap-1 text-[10px] tracking-[0.3em] text-[#A39E93]">
        {isFounding && <Sparkles size={10} style={{ color: accent }} />}
        <span data-testid="mobile-nav-members-designation">{designation}</span>
      </div>
    </Link>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { registerLogoClick } = useJericho();

  // Compact sticky state (mobile) once the user starts scrolling
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <header className="sticky top-0 z-30 bg-[#0F0F0F]/90 backdrop-blur-sm border-b border-[#2D2A26]">
      <div
        className={`max-w-7xl min-[1360px]:max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between gap-3 transition-[padding] duration-200 ${
          scrolled ? "py-2 lg:py-4" : "py-4"
        }`}
      >
        <Link
          to="/"
          data-testid="logo-link"
          className={`group min-w-0 flex-shrink min-[1360px]:shrink-0 min-[1360px]:whitespace-nowrap origin-left transition-transform duration-200 ${
            scrolled ? "scale-[0.85] lg:scale-100" : ""
          }`}
          onClick={registerLogoClick}
        >
          <LogoLockup markSize={44} className="group-hover:opacity-90 transition-opacity" />
        </Link>

        {/* Desktop nav — full row only at ≥1360px. Below that (768–1359px)
             the hamburger drawer is used: the 7-link + 3-CTA row plus the
             full logo lockup needs ~1,300px minimum, so narrower desktops
             would crush the logo (SPOTLIGHT/ARCHIVE overlap). */}
        <nav className="hidden min-[1360px]:flex items-center gap-4 2xl:gap-6">
          <NavLink to="/spotlight" className={navLinkCls} data-testid="nav-spotlight">
            // Spotlight
          </NavLink>
          <NavLink to="/archive" className={navLinkCls} data-testid="nav-archive">
            // Archive
          </NavLink>
          <NavLink to="/connections" className={navLinkCls} data-testid="nav-connections">
            // Connections
          </NavLink>
          <NavLink to="/epstein-files" className={navLinkCls} data-testid="nav-epstein">
            // Epstein Files
          </NavLink>
          <NavLink to="/submit" className={navLinkCls} data-testid="nav-submit">
            // Submit
          </NavLink>
          <NavLink to="/about" className={navLinkCls} data-testid="nav-about">
            // About
          </NavLink>
          <NavLink to="/members" className={navLinkCls} data-testid="nav-members">
            // Members
          </NavLink>
          <InboxNavLink />
        </nav>

        {/* Desktop right-side actions */}
        <div className="hidden min-[1360px]:flex items-center gap-3">
          <CommandTrigger />
          <Link
            to="/archive"
            className="flex items-center gap-2 border border-[#454038] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3] transition-colors"
            data-testid="header-search-button"
          >
            <Search size={12} /> Search Files
          </Link>
          <Link
            to="/submit"
            className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors"
            data-testid="header-submit-button"
          >
            <Plus size={12} /> New Dossier
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
          className={`min-[1360px]:hidden flex items-center justify-center border border-[#454038] text-[#E4DFD3] hover:border-[#D92D20] hover:text-[#D92D20] transition-all duration-200 ${
            scrolled ? "w-10 h-10" : "w-11 h-11"
          }`}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {open && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className={`min-[1360px]:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm ${scrolled ? "top-[56px]" : "top-[73px]"}`}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile drawer panel — capped to the viewport minus the sticky
          header so long menus (Members + Inbox + CTAs) remain fully
          reachable via internal scroll on short laptop screens. */}
      <div
        data-testid="mobile-menu-panel"
        className={`min-[1360px]:hidden fixed left-0 right-0 z-50 bg-[#0F0F0F] border-b border-[#D92D20]/40 overflow-y-auto overscroll-contain transition-transform duration-200 ease-out ${
          scrolled ? "top-[56px] max-h-[calc(100vh-56px)]" : "top-[73px] max-h-[calc(100vh-73px)]"
        } ${open ? "translate-y-0" : "-translate-y-[120%]"}`}
      >
        <nav className="max-w-7xl mx-auto px-6 py-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3 pb-3 border-b border-[#D92D20]/30">
            // THE ARCHIVE — NAV
          </div>
          <NavLink to="/" end className={mobileLinkCls} data-testid="mobile-nav-home">
            // Home
          </NavLink>
          <NavLink to="/spotlight" className={mobileLinkCls} data-testid="mobile-nav-spotlight">
            // Spotlight
          </NavLink>
          <NavLink to="/archive" className={mobileLinkCls} data-testid="mobile-nav-archive">
            // Archive · Dossiers
          </NavLink>
          <NavLink to="/connections" className={mobileLinkCls} data-testid="mobile-nav-connections">
            // Connections · Network
          </NavLink>
          <NavLink to="/epstein-files" className={mobileLinkCls} data-testid="mobile-nav-epstein">
            // Epstein Files
          </NavLink>
          <NavLink to="/submit" className={mobileLinkCls} data-testid="mobile-nav-submit">
            // Submit a lead
          </NavLink>
          <NavLink to="/about" className={mobileLinkCls} data-testid="mobile-nav-about">
            // About
          </NavLink>
          <MobileMembersEntry onNavigate={() => setOpen(false)} />
          <MobileInboxEntry onNavigate={() => setOpen(false)} />

          <div className="mt-5">
            <MobileCommandTrigger onNavigate={() => setOpen(false)} />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Link
              to="/archive"
              onClick={() => setOpen(false)}
              data-testid="mobile-search-btn"
              className="flex items-center justify-center gap-2 border border-[#454038] px-3 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3]"
            >
              <Search size={12} /> Search files
            </Link>
            <Link
              to="/submit"
              onClick={() => setOpen(false)}
              data-testid="mobile-submit-btn"
              className="flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-3 font-mono text-[10px] uppercase tracking-widest"
            >
              <Plus size={12} /> New dossier
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
